import React from "react";
import { ISystemConfig } from "../../vm";
import * as yup from "yup";
import {
  IStackTokens,
  Stack,
  Dropdown,
  DefaultButton,
  PrimaryButton,
  IDropdownOption,
} from "office-ui-fabric-react";
import CustomDrawer from "../common/CustomDrawer";
import { Formik } from "formik";
import {
  updateSystemConfig,
  addSystemConfig,
} from "../../services/system-config/SystemConfigService";
import {
  SC_SYSTEM_TYPE,
  SC_CONNECTION_TYPE,
  SC_SENSOR_TYPE,
  childrenGap,
  SC_CONNECTION_TYPE_DROPDOWN_DICT,
  SC_SERVICE_NOW_URL_TYPE,
} from "../common/Constants";
import { CustomTextField } from "../common/Library";
import { ToastContext } from "../common/ToastProvider";
import { CategoryHeader } from "../infrastructure/Misc";

const oidObjSchema = yup.object().shape({
  kw: yup.string().optional(),
  kwh: yup.string().optional(),
  amps: yup.string().optional(),
});

const sCSchema = yup.object().shape({
  systemType: yup.string().required("required"),
  connType: yup.string().required("required"),
  server: yup.string().optional(),
  user: yup.string().optional(),
  password: yup.string().optional(),
  database: yup.string().optional(),
  instanceName: yup.string().optional(),
  datacenterId: yup.string().optional(),
  oid: oidObjSchema,
});

export interface ManageSystemConfigProps {
  handleDialogClose: any;
  data?: ISystemConfig;
  handleLoading: any;
  datacenterDropdown: IDropdownOption[];
}

const ManageSystemConfig: React.FC<ManageSystemConfigProps> = ({
  handleDialogClose,
  data,
  handleLoading,
  datacenterDropdown,
}) => {
  //state
  const [state, setState] = React.useState({
    systemConfig: {
      systemType: undefined,
      connType: undefined,
      sensorType: undefined,
      serviceNowUrlType: undefined,
      server: "",
      user: "",
      password: "",
      database: "",
      instanceName: "",
      datacenterId: "",
      oid: {
        kw: "",
        kwh: "",
        amps: "",
      },
      ...data,
    },
    isLoading: false,
  } as {
    systemConfig: ISystemConfig;
  });

  //custom hooks
  const { showToast } = React.useContext(ToastContext);
  const wrapStackTokens: IStackTokens = { childrenGap: 16 };

  const closeAddDialog = (data?: ISystemConfig) => {
    handleDialogClose(data);
  };
  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.systemConfig._id ? "Update System Config" : "Add System Config"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={state.systemConfig}
            validationSchema={sCSchema}
            onSubmit={async (values: ISystemConfig, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.systemConfig._id) {
                result = await updateSystemConfig(obj);
              } else {
                result = await addSystemConfig(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
                //   this.props.toast.show(`System config ${this.state.customer._id ? "updated" : "created"} successfully`, "success");
              } else {
                showToast(
                  result.message ||
                  "Error while adding or updating system config",
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, touched, errors, values }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={SC_SYSTEM_TYPE}
                      selectedKey={values.systemType}
                      onChange={(e, item) => {
                        setFieldValue("systemType", item?.key);
                      }}
                      label="System Type"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.systemType &&
                          touched.systemType &&
                          errors.systemType) as string
                      }
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={
                        SC_CONNECTION_TYPE_DROPDOWN_DICT[values.systemType]
                      }
                      selectedKey={values.connType}
                      onChange={(e, item) => {
                        setFieldValue("connType", item?.key);
                      }}
                      label="Connection Type"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.connType &&
                          touched.connType &&
                          errors.connType) as string
                      }
                    />
                  </Stack.Item>
                  {(values.systemType === "tridium" ||
                    values.systemType === "ebi" ||
                    values.systemType === "siemens") && (
                      <Stack.Item className="custom-dropdown mb-30">
                        <Dropdown
                          options={SC_SENSOR_TYPE}
                          selectedKey={values.sensorType}
                          onChange={(e, item) => {
                            setFieldValue("sensorType", item?.key);
                          }}
                          label="Sensor Type"
                          placeholder="Select an option"
                        // errorMessage={
                        //   (errors.connType &&
                        //     touched.connType &&
                        //     errors.connType) as string
                        // }
                        />
                      </Stack.Item>
                    )}
                  {values.systemType === "service-now" && (
                    <Stack.Item className="custom-dropdown mb-30">
                      <Dropdown
                        options={SC_SERVICE_NOW_URL_TYPE}
                        selectedKey={values.serviceNowUrlType}
                        onChange={(e, item) => {
                          setFieldValue("serviceNowUrlType", item?.key);
                        }}
                        label="URL Type"
                        placeholder="Select an option"
                      // errorMessage={
                      //   (errors.connType &&
                      //     touched.connType &&
                      //     errors.connType) as string
                      // }
                      />
                    </Stack.Item>
                  )}
                  {!(
                    values.systemType === "delta" && values.connType === "snmp"
                  ) && (
                      <React.Fragment>
                        {values.systemType !== "ebi" && (
                          <>
                            <Stack.Item>
                              <CustomTextField name={"user"} label={"User"} />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"password"}
                                label={"Password"}
                                type={"password"}
                              />
                            </Stack.Item>
                          </>
                        )}
                        {!(
                          values.systemType === "service-now" &&
                          values.connType === "web-service"
                        ) && (
                            <Stack.Item>
                              <CustomTextField
                                name={"server"}
                                label={"Server"}
                                placeholder="Ex. 192.168. 1.1"
                              />
                            </Stack.Item>
                          )}
                      </React.Fragment>
                    )}
                  {(values.systemType === "gallagher" ||
                    values.systemType === "schneider" ||
                    (values.systemType === "delta" &&
                      values.connType !== "snmp")) && (
                      <Stack.Item>
                        <CustomTextField
                          name={"database"}
                          label={"Database"}
                          placeholder="Ex. Master"
                        />
                      </Stack.Item>
                    )}
                  {((values.systemType === "gallagher" &&
                    values.connType === "mssql") ||
                    (values.systemType === "schneider" &&
                      values.connType === "mssql") ||
                    (values.systemType === "delta" &&
                      values.connType === "mssql")) && (
                      <Stack.Item>
                        <CustomTextField
                          name={"instanceName"}
                          label={"Instance Name"}
                        />
                      </Stack.Item>
                    )}
                  {/* {values.systemType === "siemens" &&
                    values.connType === "rest-api" && (
                      <Stack.Item>
                        <CustomTextField
                          name={"endpoint"}
                          label={"End Point"}
                        />
                      </Stack.Item>
                    )} */}
                  {values.systemType === "delta" && values.connType === "snmp" && (
                    <Stack.Item>
                      <CategoryHeader title="OID" />
                      <Stack wrap tokens={childrenGap} className="pt-24">
                        <Stack.Item>
                          <CustomTextField name={"oid.kw"} label={"KW"} />
                        </Stack.Item>
                        <Stack.Item>
                          <CustomTextField name={"oid.kwh"} label={"KWH"} />
                        </Stack.Item>
                        <Stack.Item>
                          <CustomTextField name={"oid.amps"} label={"AMPS"} />
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                  )}
                  {(values.systemType === "siemens" ||
                    values.systemType === "tridium" ||
                    values.systemType === "delta" ||
                    values.systemType === "ganesh-computer" ||
                    values.systemType === "ebi") && (
                      <Stack.Item className="custom-dropdown mb-30">
                        <Dropdown
                          options={datacenterDropdown}
                          selectedKey={values.datacenterId}
                          onChange={(e, item) => {
                            setFieldValue("datacenterId", item?.key);
                          }}
                          label="Datacenter"
                          placeholder="Select an option"
                        // errorMessage={
                        //   (errors.connType &&
                        //     touched.connType &&
                        //     errors.connType) as string
                        // }
                        />
                      </Stack.Item>
                    )}
                  {values.systemType === "service-now" &&
                    values.connType === "web-service" &&
                    values.serviceNowUrlType === "tickets" && (
                      <>
                        <Stack.Item>
                          <CategoryHeader title="Present Month" />
                          <Stack tokens={childrenGap} className="pt-24">
                            <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.accessPermission"}
                                label={"Access Permission"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.materialMovement"}
                                label={"Material Movement"}
                              />
                            </Stack.Item>
                            {/* <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.others"}
                                label={"Others"}
                              />
                            </Stack.Item> */}
                            <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.remoteHandService"}
                                label={"Remote Hand Service"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.caseTicket"}
                                label={"Case Ticket"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"presentMonth.rhsBillableHours"}
                                label={"RHS Billing Hours"}
                              />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                        <Stack.Item>
                          <CategoryHeader title="Last Month" />
                          <Stack tokens={childrenGap} className="pt-24">
                            <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.accessPermission"}
                                label={"Access Permission"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.materialMovement"}
                                label={"Material Movement"}
                              />
                            </Stack.Item>
                            {/* <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.others"}
                                label={"Others"}
                              />
                            </Stack.Item> */}
                            <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.remoteHandService"}
                                label={"Remote Hand Service"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.caseTicket"}
                                label={"Case Ticket"}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <CustomTextField
                                name={"lastMonth.rhsBillableHours"}
                                label={"RHS Billing Hours"}
                              />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </>
                    )}
                  {values.systemType === "service-now" &&
                    values.connType === "web-service" &&
                    values.serviceNowUrlType === "authorized-contacts" && (
                      <Stack.Item>
                        <CustomTextField
                          name={"authorizedContactUrl"}
                          label={"Authorized Contact URL"}
                        />
                      </Stack.Item>
                    )}
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.systemConfig._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageSystemConfig;
